import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Buildings, Plus, PencilSimple, Trash, FloppyDisk, X, FilePdf, Eye, CheckCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', contact_email: '', contact_phone: '', address: '', gstin: '', notes: '' };

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/companies`);
      setCompanies(data);
    } catch (e) {
      logError('companies-list', e);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => { setForm(emptyForm); setAddOpen(true); };
  const openEdit = (c) => { setEditTarget(c); setForm({ ...emptyForm, ...c }); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    try {
      if (editTarget) {
        await axios.patch(`${API}/companies/${editTarget.id}`, form);
        toast.success(`Updated ${form.name}`);
        setEditTarget(null);
      } else {
        await axios.post(`${API}/companies`, form);
        toast.success(`Added ${form.name}`);
        setAddOpen(false);
      }
      fetchAll();
    } catch (err) {
      logError('companies-save', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name} from the directory? Existing bills are unaffected.`)) return;
    try {
      await axios.delete(`${API}/companies/${c.id}`);
      toast.success(`Deleted ${c.name}`);
      fetchAll();
    } catch (err) {
      logError('companies-delete', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePreviewSla = async (c) => {
    try {
      const res = await axios.get(`${API}/companies/${c.id}/sla.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) toast.error('Pop-up blocked — allow pop-ups to preview');
      else toast.success(`SLA preview opened for ${c.name}`);
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      fetchAll();
    } catch (err) {
      logError('companies-sla-preview', err);
      toast.error('Failed to preview SLA');
    }
  };

  const handleDownloadSla = async (c) => {
    try {
      const res = await axios.get(`${API}/companies/${c.id}/sla.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TulsiJobs_SLA_${c.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`SLA generated for ${c.name}`);
      fetchAll();
    } catch (err) {
      logError('companies-sla', err);
      toast.error(err.response?.data?.detail || 'Failed to generate SLA');
    }
  };

  return (
    <div data-testid="companies-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            Companies
          </h1>
          <p className="text-base font-medium text-white">
            Saved contact, address & GSTIN for the companies you bill. Auto-fills when creating a new bill.
          </p>
        </div>
        <Button
          data-testid="add-company-button"
          onClick={openAdd}
          className="bg-[#A3E635] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
        >
          <Plus size={20} weight="bold" className="mr-2" />
          Add Company
        </Button>
      </div>

      {loading ? (
        <div className="text-white font-bold">Loading…</div>
      ) : (
        <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
          <table className="w-full tracker-table" data-testid="companies-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Email</th>
                <th>Phone</th>
                <th>GSTIN</th>
                <th>SLA Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-6 text-white">No companies yet. Click <strong>Add Company</strong> to save your first.</td></tr>
              ) : companies.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`}>
                  <td className="font-bold flex items-center gap-2">
                    <Buildings size={18} weight="duotone" className="text-[#A3E635]" />
                    {c.name}
                  </td>
                  <td className="text-xs">{c.contact_email || <span className="text-[#888]">—</span>}</td>
                  <td className="text-xs">{c.contact_phone || <span className="text-[#888]">—</span>}</td>
                  <td className="text-xs">{c.gstin || <span className="text-[#888]">—</span>}</td>
                  <td>
                    {c.sla_generated_at ? (
                      <span
                        data-testid={`sla-status-${c.id}`}
                        title="Date the SLA PDF was last generated"
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#A3E635] text-black border-2 border-black rounded text-[10px] font-bold"
                      >
                        <CheckCircle size={12} weight="bold" />
                        SLA · {new Date(c.sla_generated_at + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    ) : (
                      <span data-testid={`sla-status-${c.id}`} className="inline-flex items-center px-2 py-1 bg-[#404040] text-white border-2 border-black rounded text-[10px] font-bold">
                        NO SLA
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`preview-sla-${c.id}`}
                        onClick={() => handlePreviewSla(c)}
                        title="Preview SLA Agreement in a new tab"
                        className="px-2.5 py-2 bg-[#93C5FD] text-black border-2 border-black rounded text-xs font-bold flex items-center gap-1 hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <Eye size={14} weight="bold" />
                        View SLA
                      </button>
                      <button
                        data-testid={`sla-company-${c.id}`}
                        onClick={() => handleDownloadSla(c)}
                        title="Download SLA Agreement PDF"
                        className="px-2.5 py-2 bg-[#FDE047] text-black border-2 border-black rounded text-xs font-bold flex items-center gap-1 hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <FilePdf size={14} weight="bold" />
                        SLA
                      </button>
                      <button
                        data-testid={`edit-company-${c.id}`}
                        onClick={() => openEdit(c)}
                        title="Edit"
                        className="p-2 bg-[#93C5FD] border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button
                        data-testid={`delete-company-${c.id}`}
                        onClick={() => handleDelete(c)}
                        title="Delete"
                        className="p-2 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={addOpen || !!editTarget} onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditTarget(null); } }}>
        <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              <Buildings size={26} weight="duotone" />
              {editTarget ? 'Edit Company' : 'Add Company'}
            </DialogTitle>
            <DialogDescription className="sr-only">Manage company contact details for billing</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Company Name *</Label>
              <Input
                data-testid="company-name-input"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                required
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
                <Input
                  data-testid="company-email-input"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({...form, contact_email: e.target.value})}
                  placeholder="hr@example.com"
                  className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Phone</Label>
                <Input
                  data-testid="company-phone-input"
                  value={form.contact_phone}
                  onChange={(e) => setForm({...form, contact_phone: e.target.value})}
                  placeholder="+91-9876543210"
                  className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">GSTIN</Label>
              <Input
                data-testid="company-gstin-input"
                value={form.gstin}
                onChange={(e) => setForm({...form, gstin: e.target.value})}
                placeholder="19ABCDE1234F1Z5"
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 uppercase"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Address</Label>
              <textarea
                data-testid="company-address-input"
                value={form.address}
                onChange={(e) => setForm({...form, address: e.target.value})}
                rows="3"
                placeholder="Building, Street&#10;City - Postcode"
                className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Notes</Label>
              <Input
                data-testid="company-notes-input"
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Internal notes (not on bill)"
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                data-testid="submit-company"
                className="flex-1 bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
              >
                <FloppyDisk size={18} weight="bold" className="mr-2" />
                {editTarget ? 'Save Changes' : 'Add Company'}
              </Button>
              <Button
                type="button"
                onClick={() => { setAddOpen(false); setEditTarget(null); }}
                className="bg-[#404040] text-white border-2 border-black rounded-md font-bold px-4 py-2"
              >
                <X size={18} weight="bold" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Companies;
